import React, { useState, useRef, useEffect } from "react";

const FILTER_CONFIG = [
  { label: "Matiere", key: "matiere", attribute: "attributs_compositions" },
  { label: "Label", key: "label", attribute: "attributs_labels" },
  { label: "Grammage", key: "grammage", attribute: "attributs_grammages", suffix: " gr/m²" },
  { label: "Couleur", key: "color", attribute: "attributs_couleur" },
  // { label: "Taille anses", key: "anses", attribute: "attributs_anses", suffix: " cm" },
  // { label: "Taille sac", key: "taille", attribute: "attributs_tailles" },
  // { label: "Dimensions", key: "dimensions", attribute: "attributs_dimensions" },
];

const StepsGrammage = [
  "<150", "150-280", ">280"
];

function getFilterOptionsFromProducts(products, attribute) {
  const map = new Map();
  products.forEach((product) => {
    const values = product[attribute];
    if (values) {
      if (attribute === "attributs_grammages") {
        const g = parseInt(values.Nom);
        const step = getStepFromGrammage(g);
        if (!map.has(step)) {
          map.set(step, { Nom: step });
        }
      } else {
        (Array.isArray(values) ? values : [values]).forEach((v) => {
          if (v && v.Nom && !map.has(v.Nom)) {
            map.set(v.Nom, v);
          }
        });
      }
    }
  });
  return Array.from(map.values()).sort((a, b) => a.Nom.localeCompare(b.Nom));
}

function parseSelectedFilters(searchParams) {
  const selected = {};
  FILTER_CONFIG.forEach(({ key }) => {
    const val = searchParams.get(key);
    selected[key] = val ? val.split("|") : [];
  });
  return selected;
}

function buildSearchParams(selected) {
  const params = new URLSearchParams();
  FILTER_CONFIG.forEach(({ key }) => {
    if (selected[key] && selected[key].length > 0) {
      params.set(key, selected[key].join("|"));
    }
  });
  return params;
}

function getProductValues(product, attribute) {
  const values = product[attribute];
  if (!values) return [];

  return (Array.isArray(values) ? values : [values]).map((v) => v.Nom).filter(Boolean);
}

function countMatchingProducts(products, selected, currentKey, optionName) {
  const testSelected = { ...selected, [currentKey]: [optionName] };
  return products.filter((product) =>
    FILTER_CONFIG.every(({ key, attribute }) => {
      if (testSelected[key].length === 0) return true;
      const pv = getProductValues(product, attribute);
      if (key === "grammage") {
        const g = parseInt(pv[0]);
        const step = getStepFromGrammage(g);
        return testSelected[key].includes(step);
      }
      return testSelected[key].some((s) => pv.includes(s));
    })
  ).length;
}

export function filterProducts(products, location) {
  if (typeof window === "undefined") return products;
  const searchParams = new URLSearchParams(location.search);
  const selected = parseSelectedFilters(searchParams);

  return products.filter((product) => {
    return FILTER_CONFIG.every(({ key, attribute }) => {
      if (selected[key].length === 0) return true;
      const pv = getProductValues(product, attribute);
      if (key === "grammage") {
        const g = parseInt(pv[0]);
        const step = getStepFromGrammage(g);
        return selected[key].includes(step);
      }
      return selected[key].some((s) => pv.includes(s));
    });
  });
}

export function getStepFromGrammage(g) {
  if (g < 150) return StepsGrammage[0];
  else if (g <= 280) return StepsGrammage[1];
  else return StepsGrammage[2];
}

export function ActiveFilters({ onFilterChange }) {
  if (typeof window === "undefined") return null;
  const searchParams = new URLSearchParams(window.location.search);
  const selected = parseSelectedFilters(searchParams);

  const chips = [];
  FILTER_CONFIG.forEach(({ key, label, suffix }) => {
    selected[key].forEach((value) => {
      chips.push({ key, label, value, suffix });
    });
  });

  if (chips.length === 0) return null;

  const removeFilter = (key, value) => {
    const next = { ...selected, [key]: selected[key].filter((v) => v !== value) };
    const params = buildSearchParams(next);
    const qs = params.toString();
    window.history.replaceState(null, "", window.location.pathname + (qs ? "?" + qs : ""));
    if (onFilterChange) onFilterChange();
  };

  const clearAll = () => {
    window.history.replaceState(null, "", window.location.pathname);
    if (onFilterChange) onFilterChange();
  };

  return (
    <div className="flex flex-wrap gap-2 mb-4 items-center">
      {chips.map((chip) => (
        <button
          key={`${chip.key}-${chip.value}`}
          onClick={() => removeFilter(chip.key, chip.value)}
          className="inline-flex items-center gap-1 px-4 bg-blue text-white text-xs rounded-full cursor-pointer hover:opacity-80 transition-opacity"
        >
          {chip.value}{chip.suffix || ""}
          <span className="font-bold text-base">&times;</span>
        </button>
      ))}
      <button
        onClick={clearAll}
        className=" text-red-600 underline cursor-pointer ml-1"
      >
        Tout effacer
      </button>
    </div>
  );
}

const FilterSection = ({ label, options, selected, suffix, filterKey, toggleFilter, products, allSelected }) => {
  const hasActive = selected.length > 0;
  const [isOpen, setIsOpen] = useState(hasActive);
  const contentRef = useRef(null);
  const [height, setHeight] = useState(hasActive ? "auto" : "0px");

  useEffect(() => {
    if (isOpen && contentRef.current) {
      setHeight(`${contentRef.current.scrollHeight}px`);
      const timer = setTimeout(() => setHeight("auto"), 200);
      return () => clearTimeout(timer);
    } else {
      if (contentRef.current) {
        setHeight(`${contentRef.current.scrollHeight}px`);
        requestAnimationFrame(() => setHeight("0px"));
      }
    }
  }, [isOpen]);

  return (
    <div className="border-b border-gray-200">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center justify-between w-full py-2 font-semibold text-gray-700 cursor-pointer bg-transparent border-none"
      >
        <span>{label}</span>
        <span className={`transition-transform duration-200 ${isOpen ? "rotate-180" : ""}`}>
          ▾
        </span>
      </button>
      <div
        ref={contentRef}
        style={{ height, overflow: "hidden", transition: "height 0.2s ease" }}
      >
        <div className="flex flex-col gap-1 pb-3">
          {options
            .sort((a, b) => filterKey === "grammage" ? (parseInt(a.Nom.match(/\d/)[0]) < parseInt(b.Nom.match(/\d/)[0]) ? -1 : 1) : a.Nom.localeCompare(b.Nom))
            .map((option) => {
              const isSelected = selected.includes(option.Nom);
              const count = countMatchingProducts(products, allSelected, filterKey, option.Nom);
              const isDisabled = !isSelected && count === 0;

              return (
                <label
                  key={option.id || option.Nom}
                  className={`flex items-center gap-2 leading-none ${isDisabled ? "opacity-40 cursor-default" : "cursor-pointer"}`}
                >
                  <input
                    type="checkbox"
                    checked={isSelected}
                    disabled={isDisabled}
                    onChange={() => toggleFilter(filterKey, option.Nom)}
                    className="accent-blue m-0 p-0 shadow-none w-4 h-4 flex-shrink-0 align-middle"
                  />
                  <span className="flex items-baseline gap-1">
                    <span className={isSelected ? "font-semibold text-blue" : "text-gray-700"}>
                      {option.Nom}{suffix || ""}
                    </span>
                    <span className="text-gray-400 text-xs">({count})</span>
                  </span>
                </label>
              );
            })}
        </div>
      </div>
    </div>
  );
};

const Filter = ({ products, onFilterChange }) => {
  const [formattedProducts, setFormattedProducts] = useState([]);
  const searchParams =
    typeof window !== "undefined"
      ? new URLSearchParams(window.location.search)
      : new URLSearchParams();
  const selected = parseSelectedFilters(searchParams);

  useEffect(() => {
    products.forEach((product) => {
      FILTER_CONFIG.forEach(({ attribute }) => {
        if (!product[attribute]) return;
        const values = Array.isArray(product[attribute]) ? product[attribute] : [product[attribute]];
        values.forEach((v) => {
          if (v && v.Nom === undefined && v.Label !== undefined)
            v.Nom = v.Label;
        });
      });
    });
    setFormattedProducts(products);
  }, [products]);

  const toggleFilter = (key, value) => {
    const current = [...selected[key]];
    const idx = current.indexOf(value);
    if (idx >= 0) {
      current.splice(idx, 1);
    } else {
      current.push(value);
    }
    const next = { ...selected, [key]: current };
    const params = buildSearchParams(next);
    const qs = params.toString();
    const newUrl =
      window.location.pathname + (qs ? "?" + qs : "");
    window.history.replaceState(null, "", newUrl);
    if (onFilterChange) onFilterChange();
  };

  const clearAll = () => {
    window.history.replaceState(null, "", window.location.pathname);
    if (onFilterChange) onFilterChange();
  };

  const hasAnyFilter = FILTER_CONFIG.some(
    ({ key }) => selected[key].length > 0
  );

  return (
    <div className="mb-6">
      <span className="h3 block text-base font-semibold uppercase pb-2 border-b-3 border-gray-200">
        Filtrer les produits
      </span>
      {FILTER_CONFIG.map(({ key, label, attribute, suffix }) => {
        const options = getFilterOptionsFromProducts(products, attribute);
        if (options.length === 0) return null;
        return (
          <FilterSection
            key={key}
            filterKey={key}
            label={label}
            suffix={suffix}
            options={options}
            selected={selected[key]}
            allSelected={selected}
            toggleFilter={toggleFilter}
            products={products}
          />
        );
      })}
      {hasAnyFilter && (
        <button
          onClick={clearAll}
          className="mt-3 text-sm text-red-600 underline cursor-pointer"
        >
          Effacer les filtres
        </button>
      )}
    </div>
  );
};

export default Filter;
